import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Shown while a live session waits for its profile row (AppContext `sessionPending`). Every sign-in
 * passes through this state for the length of one profile read — normally well under a second — so
 * it starts as a plain "Signing in…". Only when the read has taken longer than
 * RECONNECT_SCREEN_AFTER_MS (slow or failing database, e.g. right after a deploy when every tab
 * reloads) does it switch to "Reconnecting…" with Retry / Sign out. The session is alive either
 * way, so it never falls back to the login page (incident 2026-09-18..20).
 */
export const RECONNECT_SCREEN_AFTER_MS = 3000;

export const SessionWaitScreen: React.FC<{ onRetry: () => void; onSignOut: () => void }> = ({ onRetry, onSignOut }) => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), RECONNECT_SCREEN_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  if (!slow) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-slate-900" data-testid="session-signing-in">
        <Loader2 className="animate-spin text-brand-500 mb-4" size={48} />
        <p className="text-slate-400 font-medium animate-pulse">Signing in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-slate-900 px-6 text-center" data-testid="session-reconnecting">
      <Loader2 className="animate-spin text-brand-500 mb-4" size={48} />
      <p className="text-slate-200 font-semibold">Reconnecting…</p>
      <p className="text-slate-400 text-sm mt-1 max-w-sm">You are still signed in. The server is slow to answer — this page retries by itself, there is no need to log in again.</p>
      <div className="flex gap-3 mt-6">
        <button type="button" onClick={onRetry} className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold">Retry now</button>
        <button type="button" onClick={onSignOut} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold">Sign out</button>
      </div>
    </div>
  );
};
